/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 *
 * @format
 */

import { useEffect, useRef, type ReactNode } from "react";
import {
	motion,
	useReducedMotion,
	useMotionValue,
	useSpring,
	useTransform,
} from "motion/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { cn } from "@/lib/utils";

gsap.registerPlugin(ScrollTrigger);

/* =========================
   PAGE SHELL (CINEMATIC BASE)
========================= */

export function PageShell({
	children,
	className,
}: {
	children: ReactNode;
	className?: string;
}) {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const shouldReduceMotion = useReducedMotion();

	useEffect(() => {
		window.scrollTo(0, 0);

		if (shouldReduceMotion) return;

		const ctx = gsap.context(() => {
			// Parallax orbs
			gsap.to(".bg-orb", {
				y: 120,
				x: 60,
				ease: "none",
				scrollTrigger: {
					trigger: containerRef.current,
					start: "top top",
					end: "bottom top",
					scrub: true,
				},
			});
		}, containerRef);

		return () => ctx.revert();
	}, []);

	return (
		<motion.main
			ref={containerRef}
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
			className={cn(
				"relative min-h-screen overflow-hidden bg-slate-50 text-slate-900",
				className,
			)}>
			{/* === AMBIENT ORBS === */}
			{/* Top-left indigo */}
			<div className="bg-orb pointer-events-none absolute top-[-140px] left-[-140px] h-[420px] w-[420px] rounded-full bg-[#2d2f92]/15 blur-[80px]" />
			{/* Bottom-right gold */}
			<div className="bg-orb pointer-events-none absolute bottom-[-150px] right-[-130px] h-[380px] w-[380px] rounded-full bg-[#f9a93a]/18 blur-[70px]" />
			{/* Mid accent */}
			<div className="bg-orb pointer-events-none absolute top-[45%] left-[55%] h-[280px] w-[280px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#2d2f92]/8 blur-[60px]" />

			{/* === GRID TEXTURE === */}
			<div className="pointer-events-none absolute inset-0 opacity-[0.22] [background-image:linear-gradient(rgba(148,163,184,0.2)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.2)_1px,transparent_1px)] [background-size:48px_48px]" />

			{/* === TOP ACCENT RULE === */}
			<div className="absolute top-0 left-0 right-0 z-20 h-[2px] bg-gradient-to-r from-transparent via-[#2d2f92]/50 to-transparent" />

			<div className="relative z-10">{children}</div>
		</motion.main>
	);
}

/* =========================
   SECTION WRAPPER
========================= */

export function SectionWrapper({
	children,
	className,
	contentClassName,
	id,
}: {
	children: ReactNode;
	className?: string;
	contentClassName?: string;
	id?: string;
}) {
	return (
		<section
			id={id}
			className={cn("px-4 py-10 sm:px-6 sm:py-14 lg:px-8", className)}>
			<div className={cn("mx-auto max-w-7xl", contentClassName)}>
				{children}
			</div>
		</section>
	);
}

/* =========================
   SECTION DIVIDER
========================= */

export function SectionDivider({ className }: { className?: string }) {
	return (
		<div className={cn("mx-auto max-w-7xl px-4 sm:px-6 lg:px-8", className)}>
			<motion.div
				className="h-px w-full origin-left"
				style={{
					background:
						"linear-gradient(90deg, #2d2f92 0%, #f9a93a 50%, transparent 100%)",
				}}
				initial={{ scaleX: 0, opacity: 0 }}
				whileInView={{ scaleX: 1, opacity: 1 }}
				viewport={{ once: true, margin: "-40px" }}
				transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1] }}
			/>
		</div>
	);
}

/* =========================
   PAGE HEADER (STAGGERED)
========================= */

export function PageHeader({
	eyebrow,
	title,
	description,
	meta,
	align = "left",
	className,
}: {
	eyebrow?: string;
	title: string;
	description?: string;
	meta?: string[];
	align?: "left" | "center";
	className?: string;
}) {
	const alignClass =
		align === "center" ? "mx-auto text-center items-center" : "items-start";

	const childVariants = {
		hidden: { opacity: 0, y: 18, filter: "blur(4px)" },
		visible: {
			opacity: 1,
			y: 0,
			filter: "blur(0px)",
			transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] },
		},
	};

	return (
		<motion.div
			initial="hidden"
			animate="visible"
			variants={{
				hidden: {},
				visible: { transition: { staggerChildren: 0.09, delayChildren: 0.04 } },
			}}
			className={cn("flex max-w-3xl flex-col gap-4", alignClass, className)}>
			{/* Eyebrow badge */}
			{eyebrow && (
				<motion.div
					variants={childVariants}
					className="inline-flex items-center gap-2">
					{/* Pulsing accent dot */}
					<span className="relative flex h-2 w-2">
						<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#f9a93a] opacity-60" />
						<span className="relative inline-flex h-2 w-2 rounded-full bg-[#f9a93a]" />
					</span>
					<span className="inline-flex items-center rounded-full border border-[#2d2f92]/25 bg-white/80 px-3.5 py-1 text-[11px] font-semibold uppercase tracking-[0.28em] text-[#2d2f92] shadow-sm backdrop-blur-sm">
						{eyebrow}
					</span>
				</motion.div>
			)}

			{/* Title with gradient accent underline */}
			<motion.div
				variants={childVariants}
				className="relative">
				<h1 className="text-3xl font-semibold tracking-[-0.04em]  sm:text-4xl lg:text-[2.65rem] lg:leading-[1.18]">
					{title}
				</h1>
				{/* Decorative underline */}
				<motion.div
					className="mt-3 h-[3px] w-16 rounded-full"
					style={{
						background: "linear-gradient(90deg, #2d2f92, #f9a93a)",
					}}
					initial={{ scaleX: 0, originX: 0 }}
					animate={{ scaleX: 1 }}
					transition={{ duration: 0.7, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
				/>
			</motion.div>

			{description && (
				<motion.p
					variants={childVariants}
					className="text-sm leading-[1.85] text-slate-500 sm:text-[15px]">
					{description}
				</motion.p>
			)}

			{/* Meta tags */}
			{meta && meta.length > 0 && (
				<motion.div
					variants={childVariants}
					className="flex flex-wrap gap-2">
					{meta.map((tag) => (
						<span
							key={tag}
							className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-500 shadow-sm">
							{tag}
						</span>
					))}
				</motion.div>
			)}
		</motion.div>
	);
}

/* =========================
   REVEAL ON SCROLL
========================= */

export function RevealOnScroll({
	children,
	delay = 0,
	className,
	from = "bottom",
}: {
	children: ReactNode;
	delay?: number;
	className?: string;
	from?: "bottom" | "left" | "right";
}) {
	const shouldReduceMotion = useReducedMotion();

	const offsets = {
		bottom: { y: shouldReduceMotion ? 0 : 32, x: 0, filter: "blur(5px)" },
		left: { x: shouldReduceMotion ? 0 : -32, y: 0, filter: "blur(5px)" },
		right: { x: shouldReduceMotion ? 0 : 32, y: 0, filter: "blur(5px)" },
	};

	return (
		<motion.div
			initial={{ opacity: 0, ...offsets[from] }}
			whileInView={{ opacity: 1, y: 0, x: 0, filter: "blur(0px)" }}
			viewport={{ once: true, margin: "-64px" }}
			transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }}
			className={className}>
			{children}
		</motion.div>
	);
}

/* =========================
   CARD (HOVER MAGIC)
========================= */

export function Card({
	children,
	className,
}: {
	children: ReactNode;
	className?: string;
}) {
	const cardRef = useRef<HTMLDivElement>(null);
	const mouseX = useMotionValue(0.5);
	const mouseY = useMotionValue(0.5);

	const springCfg = { stiffness: 220, damping: 22 };
	const rotateX = useSpring(useTransform(mouseY, [0, 1], [6, -6]), springCfg);
	const rotateY = useSpring(useTransform(mouseX, [0, 1], [-6, 6]), springCfg);
	const glowX = useTransform(mouseX, (v) => `${v * 100}%`);
	const glowY = useTransform(mouseY, (v) => `${v * 100}%`);

	function onMove(e: React.MouseEvent<HTMLDivElement>) {
		const r = cardRef.current?.getBoundingClientRect();
		if (!r) return;
		mouseX.set((e.clientX - r.left) / r.width);
		mouseY.set((e.clientY - r.top) / r.height);
	}

	function onLeave() {
		mouseX.set(0.5);
		mouseY.set(0.5);
	}

	return (
		<motion.div
			ref={cardRef}
			onMouseMove={onMove}
			onMouseLeave={onLeave}
			style={{
				rotateX,
				rotateY,
				transformPerspective: 900,
				transformStyle: "preserve-3d",
			}}
			whileHover={{ y: -7, scale: 1.02 }}
			transition={{ type: "spring", stiffness: 200, damping: 18 }}
			className={cn(
				"group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm",
				"transition-shadow duration-300 hover:shadow-[0_20px_60px_-12px_rgba(45,47,146,0.18)]",
				"hover:border-[#2d2f92]/30",
				className,
			)}>
			{/* === SHIMMER SWEEP on hover === */}
			<div className="pointer-events-none absolute inset-0 -translate-x-full skew-x-[-20deg] bg-gradient-to-r from-transparent via-white/40 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-[200%]" />

			{/* === MOUSE-TRACKING GLOW === */}
			<motion.div
				className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-100"
				style={{
					background: `radial-gradient(320px circle at ${glowX} ${glowY}, rgba(45,47,146,0.08) 0%, transparent 70%)`,
				}}
			/>

			{/* === TOP BORDER REVEAL === */}
			<div className="absolute left-6 right-6 top-0 h-[2px] origin-left scale-x-0 rounded-full bg-gradient-to-r from-[#2d2f92] via-[#f9a93a] to-[#2d2f92]/0 transition-transform duration-500 ease-out group-hover:scale-x-100" />

			{/* === CORNER GLOW dot === */}
			<div className="absolute right-4 top-4 h-16 w-16 rounded-full bg-[#2d2f92]/6 blur-2xl opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

			<div className="relative z-10">{children}</div>
		</motion.div>
	);
}

/* =========================
   GRID LAYOUT
========================= */

export function GridLayout({
	children,
	className,
	columns = "three",
}: {
	children: ReactNode;
	className?: string;
	columns?: "two" | "three" | "four";
}) {
	const columnClass =
		columns === "two" ? "md:grid-cols-2"
		: columns === "four" ? "sm:grid-cols-2 xl:grid-cols-4"
		: "md:grid-cols-2 xl:grid-cols-3";

	return (
		<div className={cn("grid gap-6", columnClass, className)}>{children}</div>
	);
}

/* =========================
   TIMELINE ITEM
========================= */

export function TimelineItem({
	label,
	title,
	description,
	index = 0,
	className,
}: {
	label: string;
	title: string;
	description: string;
	index?: number;
	className?: string;
}) {
	return (
		<RevealOnScroll
			delay={index * 0.1}
			className={className}>
			<Card className="relative pl-8">
				{/* Gradient accent bar */}
				<div className="absolute left-2 top-4 bottom-4 w-[3px] overflow-hidden rounded-full bg-slate-100">
					<motion.div
						className="h-full w-full bg-gradient-to-b from-[#f9a93a] via-[#2d2f92] to-[#2d2f92]/30"
						initial={{ scaleY: 0, originY: 0 }}
						whileInView={{ scaleY: 1 }}
						viewport={{ once: true }}
						transition={{
							duration: 0.7,
							delay: index * 0.08,
							ease: [0.22, 1, 0.36, 1],
						}}
					/>
				</div>

				{/* Pulsing top dot */}
				<div className="absolute left-[3px] top-4 flex h-3.5 w-3.5 items-center justify-center">
					<span className="absolute h-full w-full animate-ping rounded-full bg-[#f9a93a]/40" />
					<span className="relative h-2 w-2 rounded-full bg-[#f9a93a] shadow-[0_0_6px_#f9a93a]" />
				</div>

				<div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#2d2f92]">
					{label}
				</div>

				<h3 className="mt-2 text-lg font-semibold text-slate-900">{title}</h3>

				<p className="mt-2 text-sm leading-7 text-slate-500">{description}</p>
			</Card>
		</RevealOnScroll>
	);
}

export function TimelineRow({
	item,
	index,
}: {
	item: Milestone;
	index: number;
}) {
	const isLeft = index % 2 === 0;

	return (
		<div className="relative flex items-center justify-center">
			{/* Center Dot */}
			<div className="absolute left-1/2 -translate-x-1/2 z-10">
				<div className="h-5 w-5 rounded-full bg-[#f9a93a] shadow-lg" />
			</div>

			{/* Card */}
			<motion.div
				initial={{ opacity: 0, x: isLeft ? -100 : 100 }}
				whileInView={{ opacity: 1, x: 0 }}
				viewport={{ once: true }}
				transition={{ duration: 0.6, ease: "easeOut" }}
				className={`w-[45%] ${
					isLeft ? "mr-auto text-right pr-10" : "ml-auto pl-10"
				}`}>
				<div className="rounded-2xl border bg-white p-6 shadow-md hover:shadow-xl transition">
					<p className="text-sm font-bold text-[#2d2f92]">{item.year}</p>
					<h3 className="text-lg font-semibold">{item.title}</h3>
					<p className="text-sm text-slate-600 mt-2">{item.description}</p>
				</div>
			</motion.div>
		</div>
	);
}

/* =========================
   STAT BADGE
========================= */

export function StatBadge({
	value,
	label,
	className,
}: {
	value: string;
	label: string;
	className?: string;
}) {
	return (
		<RevealOnScroll className={className}>
			<div className="flex flex-col items-start gap-1 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
				<motion.span
					className="text-3xl font-bold tracking-tight text-slate-900"
					initial={{ opacity: 0, scale: 0.6 }}
					whileInView={{ opacity: 1, scale: 1 }}
					viewport={{ once: true }}
					transition={{ type: "spring", stiffness: 200, damping: 14 }}>
					{value}
				</motion.span>
				<span className="text-xs font-medium uppercase tracking-widest text-slate-400">
					{label}
				</span>
				<div className="mt-1 h-[2px] w-8 rounded-full bg-gradient-to-r from-[#2d2f92] to-[#f9a93a]" />
			</div>
		</RevealOnScroll>
	);
}
